import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SynchronizationService } from './synchronization-service';
import { SynchronizationSummaryService } from './synchronization-summary.service';
import {
  SynchronizationExecutionState,
  SynchronizationTaskDefinition,
} from '../models/synchronization-status.model';
import { ClientEventService } from 'src/app/shared/services/client-event.service';
import { ClientEventEntityType } from 'src/app/shared/local-db/models/client-event';
import { SynchronizationExecutionStateEnum } from 'src/app/shared/enums/synchronization-execution-state.enum';
import { LastSuccessfulSynchronizationItem } from 'src/app/shared/local-db/models/last-successful-synchronization';
import { SynchronizationCheckpointService } from './synchronization-checkpoint.service';
import {
  SynchronizationCancelledError,
  SynchronizationRunControl,
} from '../models/synchronization-run-control.model';

export interface RunSynchronizationOptions {
  userId: string;
  userEmail: string;
  onStateChange?: (state: SynchronizationExecutionState) => void;
  mergeWithExistingSummary?: boolean;
  eventTask?: SynchronizationTaskDefinition;
  resumeFromCheckpoint?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SynchronizationExecutionService {
  private activeExecution:
    | {
        promise: Promise<LastSuccessfulSynchronizationItem[]>;
        cancel: () => void;
      }
    | null = null;
  private checkpointMutationChain = Promise.resolve();
  private readonly synchronizationCompletedSubject = new Subject<void>();
  private readonly synchronizationInProgressSubject = new BehaviorSubject<boolean>(false);
  private readonly synchronizationStateSubject =
    new BehaviorSubject<SynchronizationExecutionState>({
      items: [],
      isInProgress: false,
    });

  get synchronizationCompleted$(): Observable<void> {
    return this.synchronizationCompletedSubject.asObservable();
  }

  get synchronizationInProgress$(): Observable<boolean> {
    return this.synchronizationInProgressSubject.asObservable();
  }

  get synchronizationState$(): Observable<SynchronizationExecutionState> {
    return this.synchronizationStateSubject.asObservable();
  }

  get isSynchronizationInProgress(): boolean {
    return this.synchronizationInProgressSubject.value;
  }

  get synchronizationState(): SynchronizationExecutionState {
    return this.synchronizationStateSubject.value;
  }

  constructor(
    private readonly synchronizationService: SynchronizationService,
    private readonly clientEventService: ClientEventService,
    private readonly synchronizationSummaryService: SynchronizationSummaryService,
    private readonly synchronizationCheckpointService: SynchronizationCheckpointService
  ) {}

  async runFullSynchronization(
    options: RunSynchronizationOptions
  ): Promise<LastSuccessfulSynchronizationItem[] | null> {
    return this.runSynchronization(this.buildSynchronizationTasks(options.userEmail), {
      ...options,
      resumeFromCheckpoint: true,
    });
  }

  async runSynchronizationStep(
    labelKey: string,
    options: RunSynchronizationOptions
  ): Promise<LastSuccessfulSynchronizationItem | null> {
    const task = this.buildSynchronizationTasks(options.userEmail).find(
      synchronizationTask => synchronizationTask.labelKey === labelKey
    );

    if (!task) {
      return null;
    }

    const [result] =
      (await this.runSynchronization([task], {
        ...options,
        mergeWithExistingSummary: true,
        eventTask: task,
      })) ?? [];

    return result ?? null;
  }

  cancelActiveSynchronization(): void {
    this.activeExecution?.cancel();
  }

  resetSynchronizationState(): void {
    this.emitState([], false);
  }

  private async runSynchronization(
    tasks: SynchronizationTaskDefinition[],
    options: RunSynchronizationOptions
  ): Promise<LastSuccessfulSynchronizationItem[] | null> {
    if (this.activeExecution != null) {
      return null;
    }

    const executionControl = this.createExecutionControl();
    this.synchronizationInProgressSubject.next(true);
    this.activeExecution = {
      promise: this.executeSynchronization(tasks, options, executionControl),
      cancel: () => executionControl.cancel(),
    };

    try {
      return await this.activeExecution.promise;
    } finally {
      this.activeExecution = null;
      this.synchronizationInProgressSubject.next(false);
    }
  }

  private async executeSynchronization(
    tasks: SynchronizationTaskDefinition[],
    options: RunSynchronizationOptions,
    executionControl: ReturnType<typeof this.createExecutionControl>
  ): Promise<LastSuccessfulSynchronizationItem[]> {
    const executionContext = await this.createExecutionContext(tasks, options);
    let items = executionContext.items;
    const summaryItems = executionContext.summaryItems;
    const sequentialTasks = executionContext.tasksToRun.filter(item => item.index < 4);
    const concurrentTasks = executionContext.tasksToRun.filter(item => item.index >= 4);
    const deferredFailureLogs = new Array<
      {
        entityType: ClientEventEntityType;
        labelKey: string;
        error: unknown;
      }
    >(tasks.length);

    try {
      await this.persistCheckpointIfNeeded(options, items, executionContext.startedAt);

      executionControl.throwIfCancelled();

      await this.clientEventService.logSynchronizationExecutionStarted(
        options.userId,
        options.userEmail,
        options.eventTask
          ? {
              labelKey: options.eventTask.labelKey,
              entityType: options.eventTask.entityType,
            }
          : undefined
      );

      this.emitState(items, true, options);

      const runTask = async (
        task: SynchronizationTaskDefinition,
        index: number,
        logFailureImmediately = true
      ): Promise<void> => {
        executionControl.throwIfCancelled();

        items = this.updateSynchronizationItem(
          items,
          index,
          SynchronizationExecutionStateEnum.Running
        );
        await this.persistCheckpointIfNeeded(options, items, executionContext.startedAt);
        this.emitState(items, true, options);

        try {
          const synchronizedRecords = await task.run(executionControl);
          executionControl.throwIfCancelled();

          items = this.updateSynchronizationItem(
            items,
            index,
            SynchronizationExecutionStateEnum.Success,
            undefined,
            synchronizedRecords
          );
          summaryItems[index] = {
            labelKey: task.labelKey,
            synchronizedRecords,
            status: SynchronizationExecutionStateEnum.Success,
          };
        } catch (error) {
          if (error instanceof SynchronizationCancelledError) {
            throw error;
          }

          items = this.updateSynchronizationItem(
            items,
            index,
            SynchronizationExecutionStateEnum.Error,
            this.getErrorMessage(error)
          );
          summaryItems[index] = {
            labelKey: task.labelKey,
            synchronizedRecords: 0,
            status: SynchronizationExecutionStateEnum.Error,
          };
          if (logFailureImmediately) {
            await this.clientEventService.logSynchronizationStepFailure(
              options.userId,
              task.entityType,
              task.labelKey,
              error
            );
          } else {
            deferredFailureLogs[index] = {
              entityType: task.entityType,
              labelKey: task.labelKey,
              error,
            };
          }
        }

        await this.persistCheckpointIfNeeded(options, items, executionContext.startedAt);
        this.emitState(items, true, options);
      };

      for (const task of sequentialTasks) {
        await runTask(task.task, task.index);
      }

      executionControl.throwIfCancelled();

      await Promise.all(concurrentTasks.map(task => runTask(task.task, task.index, false)));

      executionControl.throwIfCancelled();

      for (const failureLog of deferredFailureLogs) {
        if (!failureLog) {
          continue;
        }

        await this.clientEventService.logSynchronizationStepFailure(
          options.userId,
          failureLog.entityType,
          failureLog.labelKey,
          failureLog.error
        );
      }

      if (options.mergeWithExistingSummary) {
        await this.synchronizationSummaryService.mergeSynchronizationSummary(
          options.userEmail,
          summaryItems
        );
      } else {
        await this.synchronizationSummaryService.saveSynchronizationSummary(
          options.userEmail,
          summaryItems
        );
      }
      await this.clientEventService.logSynchronizationExecutionFinished(
        options.userId,
        options.userEmail,
        summaryItems,
        options.eventTask
          ? {
              labelKey: options.eventTask.labelKey,
              entityType: options.eventTask.entityType,
            }
          : undefined
      );
      await this.clientEventService.deleteExpiredEvents(options.userId);
      await this.clearCheckpointIfNeeded(options);

      this.synchronizationCompletedSubject.next();
      this.emitState(items, false, options);

      return summaryItems;
    } catch (error) {
      if (error instanceof SynchronizationCancelledError) {
        items = this.normalizeCancelledItems(items);
        await this.persistCheckpointIfNeeded(options, items, executionContext.startedAt);
        this.emitState(items, false, options);
        return summaryItems;
      }

      this.emitState(items, false, options);
      throw error;
    }
  }

  private buildSynchronizationTasks(userEmail: string): SynchronizationTaskDefinition[] {
    return [
      {
        labelKey: '::DosageDeviation:Synchronization:Step:User',
        entityType: ClientEventEntityType.User,
        run: control => this.synchronizationService.SyncUserData(userEmail, control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:SendPending',
        entityType: ClientEventEntityType.ServiceOrder,
        run: control => this.synchronizationService.SendPendingData(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:SendPendingDeviationResolutions',
        entityType: ClientEventEntityType.SupplyDeviation,
        run: control => this.synchronizationService.SendPendingSupplyDeviationResolutions(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:ServiceOrders',
        entityType: ClientEventEntityType.ServiceOrder,
        run: control => this.synchronizationService.SyncServiceOrders(userEmail, control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:Region',
        entityType: ClientEventEntityType.Region,
        run: control => this.synchronizationService.SyncRegion(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:Resources',
        entityType: ClientEventEntityType.Resource,
        run: control => this.synchronizationService.SyncResources(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:Reasons',
        entityType: ClientEventEntityType.Reason,
        run: control => this.synchronizationService.SyncReasons(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:Vendor',
        entityType: ClientEventEntityType.Vendor,
        run: control => this.synchronizationService.SyncVendor(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:Property',
        entityType: ClientEventEntityType.Property,
        run: control => this.synchronizationService.SyncProperty(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:Stand',
        entityType: ClientEventEntityType.Stand,
        run: control => this.synchronizationService.SyncStand(control),
      },
      {
        labelKey: '::DosageDeviation:Synchronization:Step:SupplyDeviations',
        entityType: ClientEventEntityType.SupplyDeviation,
        run: control => this.synchronizationService.SyncSupplyDeviations(userEmail, control),
      },
    ];
  }

  private updateSynchronizationItem(
    items: LastSuccessfulSynchronizationItem[],
    index: number,
    status: SynchronizationExecutionStateEnum,
    errorMessage?: string,
    synchronizedRecords?: number
  ): LastSuccessfulSynchronizationItem[] {
    return items.map((item, currentIndex) =>
      currentIndex === index
        ? {
            ...item,
            status,
            errorMessage,
            synchronizedRecords: synchronizedRecords ?? item.synchronizedRecords,
          }
        : item
    );
  }

  private getErrorMessage(error: unknown): string | undefined {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return undefined;
  }

  private async createExecutionContext(
    tasks: SynchronizationTaskDefinition[],
    options: RunSynchronizationOptions
  ): Promise<{
    items: LastSuccessfulSynchronizationItem[];
    summaryItems: LastSuccessfulSynchronizationItem[];
    tasksToRun: Array<{ task: SynchronizationTaskDefinition; index: number }>;
    startedAt?: Date;
  }> {
    if (!options.resumeFromCheckpoint) {
      const summaryItems = tasks.map(task => this.createPendingSynchronizationItem(task.labelKey));

      return {
        items: this.buildInitialExecutionItems(summaryItems),
        summaryItems,
        tasksToRun: tasks.map((task, index) => ({ task, index })),
      };
    }

    const checkpoint = await this.synchronizationCheckpointService.getCheckpoint(options.userEmail);
    const checkpointItemMap = new Map(
      (checkpoint?.items ?? []).map(item => [item.labelKey, item] as const)
    );

    const summaryItems = tasks.map(task =>
      this.normalizeCheckpointItem(checkpointItemMap.get(task.labelKey), task.labelKey)
    );
    const tasksToRun = tasks
      .map((task, index) => ({ task, index }))
      .filter(
        ({ task }) =>
          checkpointItemMap.get(task.labelKey)?.status !== SynchronizationExecutionStateEnum.Success
      );

    return {
      items: this.buildInitialExecutionItems(summaryItems),
      summaryItems,
      tasksToRun,
      startedAt: checkpoint?.startedAt,
    };
  }

  private createPendingSynchronizationItem(labelKey: string): LastSuccessfulSynchronizationItem {
    return {
      labelKey,
      status: SynchronizationExecutionStateEnum.Pending,
      errorMessage: null,
      synchronizedRecords: 0,
    };
  }

  private normalizeCheckpointItem(
    item: LastSuccessfulSynchronizationItem | undefined,
    labelKey: string
  ): LastSuccessfulSynchronizationItem {
    if (item?.status === SynchronizationExecutionStateEnum.Success) {
      return {
        ...item,
        errorMessage: null,
      };
    }

    return this.createPendingSynchronizationItem(labelKey);
  }

  private async persistCheckpointIfNeeded(
    options: RunSynchronizationOptions,
    items: LastSuccessfulSynchronizationItem[],
    startedAt?: Date
  ): Promise<void> {
    if (!options.resumeFromCheckpoint) {
      return;
    }

    await this.enqueueCheckpointMutation(() =>
      this.synchronizationCheckpointService.saveCheckpoint(options.userEmail, items, startedAt)
    );
  }

  private async clearCheckpointIfNeeded(options: RunSynchronizationOptions): Promise<void> {
    if (!options.resumeFromCheckpoint) {
      return;
    }

    await this.enqueueCheckpointMutation(() =>
      this.synchronizationCheckpointService.clearCheckpoint(options.userEmail)
    );
  }

  private buildInitialExecutionItems(
    summaryItems: LastSuccessfulSynchronizationItem[]
  ): LastSuccessfulSynchronizationItem[] {
    return summaryItems.map(item => ({ ...item }));
  }

  private async enqueueCheckpointMutation(mutation: () => Promise<void>): Promise<void> {
    const nextMutation = this.checkpointMutationChain.then(mutation);
    this.checkpointMutationChain = nextMutation.catch(() => undefined);
    await nextMutation;
  }

  private normalizeCancelledItems(
    items: LastSuccessfulSynchronizationItem[]
  ): LastSuccessfulSynchronizationItem[] {
    return items.map(item =>
      item.status === SynchronizationExecutionStateEnum.Running
        ? {
            ...item,
            status: SynchronizationExecutionStateEnum.Pending,
            errorMessage: null,
          }
        : item
    );
  }

  private emitState(
    items: LastSuccessfulSynchronizationItem[],
    isInProgress: boolean,
    options?: RunSynchronizationOptions
  ): void {
    const nextState = {
      items: items.map(item => ({ ...item })),
      isInProgress,
    };

    this.synchronizationInProgressSubject.next(isInProgress);
    this.synchronizationStateSubject.next(nextState);
    options?.onStateChange?.(nextState);
  }

  private createExecutionControl(): SynchronizationRunControl & { cancel: () => void } {
    const cancellationSubject = new Subject<void>();
    let isCancelled = false;

    return {
      cancellation$: cancellationSubject.asObservable(),
      get isCancelled() {
        return isCancelled;
      },
      throwIfCancelled() {
        if (isCancelled) {
          throw new SynchronizationCancelledError();
        }
      },
      cancel() {
        if (isCancelled) {
          return;
        }

        isCancelled = true;
        cancellationSubject.next();
        cancellationSubject.complete();
      },
    };
  }
}
